import axios from 'axios';
import authService from './authService';

const API_BASE_URL = '/api';

// 🔹 創建 Axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

// ✅ 版本更新時需要清除的所有登入相關 localStorage key
const AUTH_STORAGE_KEYS = [
    'authToken',
    'tokenExpiration',
    'tokenType',
    'roles',
    'authData',
    'userId',
    'userName',
    'nickName',
    'userRoles',
    'auth',
    'user',
    'permissions',
    'user_permissions',
    'menu_permissions',
];

export const clearAuthStorage = () => {
    AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
};

// ✅ Cache Busting：取得後端 App 版本號
export const getAppVersion = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/Version`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
            }
        });
        return response.data?.appVersion || response.data?.version || null;
    } catch (error) {
        return null;
    }
};

// 🔹 設定攔截器，讓每個請求自動帶入 Token
apiClient.interceptors.request.use(

    (config) => {
        const token = localStorage.getItem("authToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 🔹 Response 攔截器 — 401 自動登出並導向登入頁
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/Auth/login')) {
            // 清除所有登入狀態
            const roles = JSON.parse(localStorage.getItem('roles') || '[]');
            const isSuperAdmin = roles.some(r => r.toLowerCase() === 'superadmin');
            clearAuthStorage();
            window.location.href = isSuperAdmin ? '/superadmin' : '/';
        }
        return Promise.reject(error);
    }
);


// 🔹 API 請求函式
export const login = async (userName, password) => {
    try {
        const response = await apiClient.post(`/Auth/login`, { userName, password });

        console.log('🚀 API Response:', response.data);

        // 🆕 使用 authService 處理登入回應和權限儲存（新功能）
        authService.login(response.data);

        // 🔄 保持向下相容：繼續設定原有的 localStorage 項目
        const { Token, Type, Expiration, UserId, UserName, NickName, Roles } = response.data;
        localStorage.setItem("authToken", Token);
        localStorage.setItem("tokenType", Type);
        localStorage.setItem("tokenExpiration", Expiration);
        localStorage.setItem("userId", UserId);
        localStorage.setItem("userName", UserName);
        localStorage.setItem("nickName", NickName);
        localStorage.setItem("userRoles", Roles);

        console.log('✅ Login processed with both old and new permission systems');

        return response.data;
    } catch (error) {
        console.error('❌ Login API Error:', error);
        throw error;
    }
};


export const getUsers = async () => {
    try {
        const token = localStorage.getItem("authToken");
        if (!token) {
            throw new Error("無法發送請求，Token 不存在!");
        }

        const response = await apiClient.get(`/Users`, {
            headers: {
                Authorization: `Bearer ${token}` // ✅ 確保 Token 有附帶
            }
        });

        return response.data;
    } catch (error) {
        console.error("getUsers API 錯誤:", error);
        throw error;
    }
};



//  取得單一使用者資料
export const getUserById = async (id) => {
    try {
        const response = await apiClient.get(`/Users/${id}`);

        console.log(`取得 ID 為 ${id} 的使用者資料:`, response.data);
        return response.data;
    } catch (error) {
        console.error("getUserById API 錯誤:", error);
        throw error;
    }
};

//  取得指定使用者的 Listings
export const getListingsByUserId = async (userId) => {
    try {
        const response = await apiClient.get(`/UserListings/user/${userId}`);

        console.log("API 回傳的 UserListing:", response.data);
        return response.data.Listings || [];
    } catch (error) {
        console.error("取得 Listings 失敗:", error.response?.data || error.message);
        throw error;
    }
};




// 更新Setting Page User密碼
export const updatePassword = async (userId, newPassword) => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.put(`/Users/${userId}`, [
        {
            op: "replace",
            path: "/password",
            value: newPassword
        }
    ], {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json"
        }
    });

    return response.data;
};


export const updateUser = async (userId, updateFields) => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.put(`/Users/${userId}`, updateFields, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json-patch+json"
        }
    });

    return response.data;
};


// 取得 Calendar 資料
export const getCalendarData = async (listingId, startDate, endDate, perPage = 100, page = 1) => {
    try {
        const token = localStorage.getItem("authToken");
        if (!token) {
            console.error("Token 不存在，無法發送請求");
            throw new Error("Token 不存在");
        }

        if (!listingId) {
            console.error("listingId 不存在，無法取得行事曆資料");
            throw new Error("listingId 必須提供");
        }

        const lid = Number(listingId);
        const params = {
            listingId: lid,
            ListingId: lid,
            startDate,
            StartDate: startDate,
            endDate,
            EndDate: endDate,
            perPage,
            page
        };

        console.log("正在請求行事曆 API:", `/Calendar`, params);

        const response = await apiClient.get(`/Calendar`, { params });

        console.log("📅 行事曆資料獲取成功:", response.data);
        return response.data;
    } catch (error) {
        console.error("取得行事曆資料失敗:", error.response?.data || error.message);
        throw error;
    }
};
// 取得 Calendar&Reservation 資料
export const getCalendarBatchData = async ({
    listingIds,
    startDate,
    endDate,
    perPage = 31,
    page = 1
}) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.post(
        `/Calendar/batch`,
        listingIds.map(Number),
        {
            params: {
                startDate,
                endDate,
                perPage,
                page
            },
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json-patch+json"
            }
        }
    );

    return response.data;
};

// Debug: 獲取 Hostify IDs
export const debugHostifyIds = async () => {
    const response = await apiClient.post(`/Admin/users/debug/hostify-ids`);
    return response.data;
};

// 取得單一 Listing 資料
export const getListingById = async (listingId) => {
    try {
        const response = await apiClient.get(`/Listings/${listingId}`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getListingById 錯誤：", message);
        throw new Error(message);
    }
};

// 取得parent + childern URL資料
export const getListingPlatformUrls = async (parentListingId) => {
    try {
        const response = await apiClient.get(`/Listings/${parentListingId}/platform-urls`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getListingPlatformUrls 錯誤：", message);
        throw new Error(message);
    }
};

export const getListingDetail = async (listingId) => {
    try {
        const response = await apiClient.get(`/Listings/${listingId}/detail`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getListingDetail 錯誤：", message);
        throw new Error(message);
    }
};


// 專給 Listing 頁面用，帶 includeChildren
export const getListingsWithChildren = async (userId) => {
    try {
        const response = await apiClient.get(`/UserListings/user/${userId}`, {
            params: {
                detail: true,
                includeChildren: true
            }
        });

        console.log("API 回傳的 UserListing (with children):", response.data);
        return response.data;
    } catch (error) {
        console.error("取得 Listings 失敗:", error.response?.data || error.message);
        throw error;
    }
};



// 刪除指定 User 的單一 Listing
export const deleteUserListingById = async (userId, listingId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.delete(`/UserListings`, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        data: [{ userId: Number(userId), listingId: Number(listingId) }]
    });

    console.log(`✅ 刪除 User ${userId} 的 Listing ${listingId}`);
    return response.data;
};

// 批次新增 User Listings
export const addUserListingsBatch = async (userId, listingIds) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.post(
        `/UserListings/batch`,
        {
            userListings: listingIds.map(listingId => ({
                userId: Number(userId),
                listingId: Number(listingId),
            }))
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    console.log(`✅ 批次新增 Listings to User ${userId}:`, response.data);
    return response.data;
};


// 取得指定 Listing 的 Children 列表
export const getListingChildrenById = async (listingId) => {
    try {
        const response = await apiClient.get(`/Listings/${listingId}/children`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getListingChildrenById 錯誤：", message);
        throw new Error(message);
    }
};

// 取得 Listing 的擁有者 User ID 列表
export const getListingOwnerIds = async (listingId) => {
    try {
        const response = await apiClient.get(`/Listings/${listingId}/users`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getListingOwnerIds 錯誤：", message);
        throw new Error(message);
    }
};

// 新增getUserListingsByListingId
export const getUserListingsByListingId = async (listingId) => {
    try {
        const response = await apiClient.get(`/UserListings/listing/${listingId}`);
        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message || "Unknown error";
        console.error("❌ getUserListingsByListingId 錯誤：", message);
        throw new Error(message);
    }
};


// 取得指定 listingId、日期範圍內的所有預訂資料
export const getReservationDetails = async (params, token) => {
    const { listingId, startDate } = params;

    const url = new URL(`${API_BASE_URL}/Reservations/listing/${listingId}`);
    if (startDate) url.searchParams.append("startDate", startDate);

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    if (!response.ok) {
        throw new Error("Failed to fetch reservation details");
    }

    return await response.json();
};




/// 取得 Reporting V2（一次回傳所有月份）
export const getBatchReportingData = async (listingIds, startDate, endDate) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await axios.post(
        `${API_BASE_URL}/Reporting/V2`,
        listingIds.map(Number),
        {
            params: {
                startDate,
                endDate,
                perPage: 300,
                page: 1
            },
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );
    return response.data;
};


// 取得 Reservations資料
export const getReservationsByListingId = async ({ listingId, startDate, endDate, page, perPage, token }) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/Reservations/hostify/listing/${listingId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    startDate,
                    endDate,
                    page,
                    perPage,
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error("❌ Failed to fetch Reservation API:", error);
        throw error;
    }
};

// 取得單一 Reservation 詳細資料
export const getReservationById = async (reservationId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await axios.get(`${API_BASE_URL}/Reservations/hostify/${reservationId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};


// 同步User Listing
export const syncAdminUserListingsWithUserData = async () => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.post(`/Admin/userlistings/sync-new`, null, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data;
};

// 同步 Hostify 上所有「新使用者」
export const syncAdminUsersNew = async () => {
    const token = localStorage.getItem("authToken");
    const response = await apiClient.post(`/Admin/users/sync-new`, null, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};

export const syncUserByHostifyId = async (hostifyId) => {
    const token = localStorage.getItem("authToken");
    const response = await apiClient.post(`/Admin/users/hostify/${hostifyId}/sync`, null, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
};

// 同步單一 Listing by ID
export const syncAdminListingById = async (listingId) => {
    const token = localStorage.getItem("authToken");
    if (!listingId) throw new Error("listingId 必填");
    const response = await apiClient.post(`/Admin/listings/${listingId}/sync`, null, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};



export const syncAdminListings = async () => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.post(`/Admin/listings/sync-new`, null, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    return response.data;
};



// 同步Admin Setting 單一使用者資料
export const adminSyncUserById = async (userId) => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.post(`/Admin/users/${userId}/sync`, null, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    return response.data;
};

// 批次設定/取消測試帳號
export const batchSetTestUser = async (userIds, isTestUser) => {
    const response = await apiClient.put(`/Users/batch/test-user`, {
        userIds,
        isTestUser,
    });
    return response.data;
};

// Reset 使用者密碼為預設值
export const resetUserPassword = async (userId) => {
    const token = localStorage.getItem("authToken");

    const response = await apiClient.post(`/Admin/users/${userId}/reset-password`, null, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    return response.data;
};


// 取得全部 Listings
export const getAllListings = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Listings`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
};



// 取得使用者的 Roles 和 Permissions
export const getUserPermissions = async (userId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Users/${userId}/permissions`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    console.log(`✅ 取得使用者 ${userId} 的權限資料:`, response.data);
    return response.data;
};

// 更新使用者的 Role
export const updateUserRole = async (userId, roleName) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.post(`/Users/${userId}/roles`,
        { roleName },  // 或根據後端要求的格式調整
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    console.log(`✅ User ${userId} role updated to ${roleName}`);
    return response.data;
};





// 取得使用者的 Roles (返回 RoleId 陣列)
export const getUserRoles = async (userId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Users/${userId}/roles`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    console.log(`✅ 取得使用者 ${userId} 的 Roles:`, response.data);
    return response.data;
};

// 為使用者新增 Role
export const addUserRole = async (userId, roleId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.post(`/Users/${userId}/roles`,
        { roleId },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    console.log(`✅ 為使用者 ${userId} 新增 Role ${roleId}`);
    return response.data;
};

// 移除使用者的 Role
export const removeUserRole = async (userId, roleId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.delete(`/Users/${userId}/roles/${roleId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    console.log(`✅ 移除使用者 ${userId} 的 Role ${roleId}`);
    return response.data;
};

// 更新 Role 名稱與描述
export const updateRole = async (roleId, name, description) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.put(`/Roles/${roleId}`,
        { name, description },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    console.log(`✅ 更新 Role ${roleId}:`, response.data);
    return response.data;
};

// 更新使用者的 Additional Permissions (批次)
export const updateUserPermissions = async (userId, permissions) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    console.log('\n🔧 updateUserPermissions - Processing...');
    console.log('Input permissions:', permissions);

    // Scope 對應
    const scopeMap = {
        'Own': 1,
        'All': 2
    };

    // 將 permissions 物件轉換成 API 需要的格式
    const permissionsArray = Object.entries(permissions).map(([permissionName, scopeText]) => {
        const scope = scopeMap[scopeText] || 1;
        console.log(`  🔄 ${permissionName}: "${scopeText}" → scope: ${scope}`);
        return {
            permissionName,
            scope
        };
    });

    const requestBody = {
        permissions: permissionsArray
    };

    console.log('\n📤 Final Request Body:');
    console.log(JSON.stringify(requestBody, null, 2));

    const response = await apiClient.post(`/Users/${userId}/permissions/batch`,
        requestBody,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    console.log('\n📥 API Response:');
    console.log(JSON.stringify(response.data, null, 2));
    console.log(`✅ User ${userId} permissions updated`);

    return response.data;
};

// 移除使用者的單一權限
export const deleteUserPermission = async (userId, permissionName) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    console.log(`🗑️ DELETE /Users/${userId}/permissions/${permissionName}`);

    const response = await apiClient.delete(`/Users/${userId}/permissions/${permissionName}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });

    console.log(`✅ Permission ${permissionName} deleted successfully`);
    return response.data;
};


// 用 batch POST + Scope 0 來移除使用者的多個權限
export const removeUserPermissionsBatch = async (userId, permissionNames) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    console.log(`🗑️ Removing permissions via batch (Scope: 0):`, permissionNames);

    const response = await apiClient.post(
        `/Users/${userId}/permissions/batch`,
        {
            permissions: permissionNames.map(name => ({
                PermissionName: name,
                Scope: 0
            }))
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );

    console.log(`✅ Permissions removed via batch:`, permissionNames);
    return response.data;
};


// Edit Price

export const updateCalendarPricesBulk = async (priceUpdates) => {
    try {
        const token = localStorage.getItem("authToken");
        if (!token) {
            throw new Error("Token 不存在，無法發送請求");
        }

        console.log("正在發送價格更新請求:", priceUpdates);

        const response = await apiClient.put(`/Calendar/bulk`, priceUpdates, {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        console.log("Edit Price Su:", response.data);
        return response.data;
    } catch (error) {
        console.error("❌ Edit Price Fail:", error.response?.data || error.message);
        throw error;
    }
};

// 取得所有 Roles
export const getRoles = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Roles`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    console.log(`✅ 取得所有 Roles:`, response.data);
    return response.data;
};

// 取得所有 Permissions
export const getAllPermissions = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Permissions`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    console.log(`✅ 取得所有 Permissions:`, response.data);
    return response.data;
};

// 取得綁定指定 Role 的所有使用者（用於刪除 Role 前的檢查）
export const getRoleUsers = async (roleId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Roles/${roleId}/Users`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    return response.data;
};

// 取得指定 Role 的 Permissions
export const getRolePermissions = async (roleId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.get(`/Roles/${roleId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    console.log(`✅ 取得 Role ${roleId} 的 Permissions:`, response.data);
    return response.data;
};

// 新增 Permission 到指定 Role (batch)
export const addRolePermission = async (roleId, permissionName, scope = "All") => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const scopeMap = { All: 2, Own: 1, None: 0 };

    const response = await apiClient.post(
        `/Roles/${roleId}/permissions/batch`,
        {
            permissions: [
                { permissionName, scope: scopeMap[scope] ?? 2 }
            ]
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    console.log(`✅ 新增 Permission "${permissionName}" 到 Role ${roleId} (batch)`);
    return response.data;
};


// 移除指定 Role 的 Permission
export const removeRolePermission = async (roleId, permissionName) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.delete(
        `/Roles/${roleId}/permissions/${permissionName}`,
        {
            headers: { Authorization: `Bearer ${token}` }
        }
    );

    console.log(`✅ 移除 Role ${roleId} 的 Permission "${permissionName}"`);
    return response.data;
};

// 更新指定 Role 的 Permission Scope (All / Own / None) — 用 batch POST，已存在則更新
export const updateRolePermissionScope = async (roleId, permissionName, scope) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const scopeMap = { All: 2, Own: 1, None: 0 };

    const response = await apiClient.post(
        `/Roles/${roleId}/permissions/batch`,
        {
            permissions: [
                { permissionName, scope: scopeMap[scope] ?? 2 }
            ]
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    console.log(`✅ 更新 Role ${roleId} 的 Permission "${permissionName}" scope → ${scope}`);
    return response.data;
};

// 刪除 Role
export const deleteRole = async (roleId) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.delete(`/Roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    console.log(`✅ 刪除 Role ${roleId}`);
    return response.data;
};

// 建立新 Role
export const createRole = async (name, description) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.post(`/Roles`,
        { name, description },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    console.log(`✅ 建立新 Role "${name}":`, response.data);
    return response.data;
};


// 取得 Platform 所有可用值
export const getListingPlatforms = async () => {
    const token = localStorage.getItem("authToken");
    const response = await apiClient.get(`/Listings/platforms`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // ["Airbnb", "Booking", ...]
};


// 批次更新 Listing 資料
export const updateListingsBatch = async (payload) => {
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("Token 不存在");

    const response = await apiClient.patch(`/Listings/batch`, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    return response.data;
};



















// ========================================
// Admin Attentions 
// ========================================

// DELETE

export const deleteAllUsers = async () => {
    const response = await apiClient.delete(`/Admin/users/clear`);
    return response.data;
};

export const deleteUserListings = async () => {
    const response = await apiClient.delete(`/Admin/userlistings/clear`);
    return response.data;
};

export const deleteListings = async () => {
    const response = await apiClient.delete(`/Admin/listings/clear`);
    return response.data;
};

export const deleteReservations = async () => {
    const response = await apiClient.delete(`/Admin/reservations/clear`);
    return response.data;
};


//INIT
export const initUsers = async () => {
    const response = await apiClient.post(`/Admin/users/init`);
    return response.data;
};

export const initListings = async () => {
    const response = await apiClient.post(`/Admin/listings/init`);
    return response.data;
};

export const initUserListings = async () => {
    const response = await apiClient.post(`/Admin/userlistings/init`);
    return response.data;
};



export const initReservations = async () => {
    const response = await apiClient.post(`/Admin/reservations/init`);
    return response.data;
};

export const syncAllListings = async () => {
    const response = await apiClient.post(`/Admin/listings/sync-all`);
    return response.data;
};

// 取得目前 sync job 狀態（current）
export const getSyncCurrentJob = async () => {
    const response = await apiClient.get(`/Admin/listings/sync-jobs/current`);
    return response.data;
};

// 依 jobId 查詢 sync job 狀態
export const getSyncJobById = async (jobId) => {
    const response = await apiClient.get(`/Admin/listings/sync-jobs/${jobId}`);
    return response.data;
};

// 匯入 Listing PlatformUrls（從 xlsx 檔案）
export const importListingPlatformUrls = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/imports/listing-platform-urls`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};


// Debug: 檢查遺漏的資料
export const debugMissingUsers = async () => {
    const response = await apiClient.get(`/Admin/users/debug/missing`);
    return response.data;
};

export const debugMissingListings = async () => {
    const response = await apiClient.get(`/Admin/listings/debug/missing`);
    return response.data;
};

export const debugMissingReservations = async () => {
    const response = await apiClient.get(`/Admin/reservations/debug/missing`);
    return response.data;
};

export const debugMissingUserListings = async () => {
    const response = await apiClient.get(`/Admin/userlistings/debug/missing`);
    return response.data;
};

// ── Maintenance ────────────────────────────────────────────────────────────

export const getMaintenanceStatus = async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.get(`${API_BASE_URL}/Maintenance`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
};

export const enableMaintenance = async ({ message = '', recoveryTimeUtc = null } = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/Maintenance/enable`,
        { message, recoveryTimeUtc },
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
};

export const disableMaintenance = async ({ message = '' } = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/Maintenance/disable`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
};

export const updateRecoveryTime = async ({ recoveryTimeUtc }) => {
    const token = localStorage.getItem('authToken');
    const response = await axios.patch(`${API_BASE_URL}/Maintenance/recovery-time`,
        { recoveryTimeUtc },
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
};

export const invalidateTokens = async () => {
    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${API_BASE_URL}/Maintenance/update-token-valid-after`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
};


// ========================================
// Audit Logs (User Activity Logs)
// ========================================

/**
 * 查詢稽核紀錄
 * GET /api/Admin/audit-logs
 * @param {Object} params
 * @param {number}  [params.actorUserId]  - 操作者使用者 ID
 * @param {number}  [params.targetUserId] - 目標使用者 ID
 * @param {string}  [params.eventType]    - 稽核事件類型
 * @param {string}  [params.entityType]   - 稽核實體類型
 * @param {string}  [params.entityId]     - 稽核實體 ID
 * @param {string}  [params.fromUtc]      - 查詢起始 UTC 時間 (ISO 8601)
 * @param {string}  [params.toUtc]        - 查詢結束 UTC 時間 (ISO 8601)
 * @param {number}  [params.page=1]       - 頁碼，從 1 開始
 * @param {number}  [params.perPage=50]   - 每頁筆數
 * @param {number}  [params.limit]        - 舊版相容，會覆蓋 perPage
 */
export const getAuditLogs = async (params = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Token 不存在');

    // 過濾掉空值，避免送出多餘的 query string
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );

    const response = await apiClient.get('/Admin/audit-logs', {
        params: cleanParams,
        headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
};

/**
 * 取得稽核事件類型清單（供下拉選單使用）
 * GET /api/Admin/audit-logs/event-types
 */
export const getAuditLogEventTypes = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Token 不存在');

    const response = await apiClient.get('/Admin/audit-logs/event-types', {
        headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
};

/**
 * 取得稽核實體類型清單（供下拉選單使用）
 * GET /api/Admin/audit-logs/entity-types
 */
export const getAuditLogEntityTypes = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Token 不存在');

    const response = await apiClient.get('/Admin/audit-logs/entity-types', {
        headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
};